import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import SecondaryNav from "@/components/SecondaryNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, MessageSquare, Sparkles, Edit3 } from "lucide-react";
import StrategyDialog from "@/pages/StrategyDialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const TRADE_LABEL = {
  scalp: "Scalp",
  daytrade: "Daytrade",
  swingtrade: "Swingtrade",
  hodl: "Hodl",
};

export default function Strategies() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("uebersicht");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resumeStrategy, setResumeStrategy] = useState(null);
  const [resumeMode, setResumeMode] = useState("form"); // "form" | "reflect"
  const [confirmDelete, setConfirmDelete] = useState(null); // id oder null

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/strategies`, { withCredentials: true });
      setItems(res.data);
    } catch (e) {
      toast.error("Konnte Strategien nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const active = useMemo(() => items.filter((s) => s.status === "aktiv"), [items]);
  const drafts = useMemo(() => items.filter((s) => s.status === "entwurf"), [items]);
  const archived = useMemo(() => items.filter((s) => s.status === "archiviert"), [items]);

  const tabs = [
    { key: "uebersicht", label: "Übersicht", count: items.length },
    { key: "aktive", label: "Aktive", count: active.length },
    { key: "entwuerfe", label: "Entwürfe", count: drafts.length },
    { key: "archiviert", label: "Archiviert", count: archived.length },
  ];

  const list =
    tab === "aktive"
      ? active
      : tab === "entwuerfe"
      ? drafts
      : tab === "archiviert"
      ? archived
      : items;

  const handleCreated = (created) => {
    setItems((prev) => {
      const exists = prev.some((p) => p.id === created.id);
      if (exists) return prev.map((p) => (p.id === created.id ? created : p));
      return [created, ...prev];
    });
    setTab(
      created.status === "aktiv"
        ? "aktive"
        : created.status === "archiviert"
        ? "archiviert"
        : "entwuerfe"
    );
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/strategies/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success("Strategie gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Strategien"
        title="Strategien"
        description="Deine Regelwerke, gemeinsam mit dem KI-Coach entwickelt."
        testid="strategies-header"
        actions={
          <Button
            className="rounded-full h-11 px-5"
            data-testid="new-strategy-btn"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Neue Strategie
          </Button>
        }
      />

      <SecondaryNav tabs={tabs} active={tab} onChange={setTab} testid="strategies-secondary-nav" />

      {loading ? (
        <div className="card-surface p-10 text-center text-muted-foreground" data-testid="strategies-loading">
          Wird geladen…
        </div>
      ) : list.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="strategies-empty">
          <Sparkles className="mx-auto h-6 w-6 text-primary-foreground/70" strokeWidth={1.5} />
          <div className="font-heading text-lg mt-3">
            {tab === "uebersicht"
              ? "Noch keine Strategie angelegt"
              : "Nichts in diesem Tab"}
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {tab === "uebersicht"
              ? "Beschreibe deine erste Strategie — der KI-Coach begleitet dich beim Anlegen und fragt nach, wo etwas unklar ist."
              : "Lege eine neue Strategie an, um sie hier zu sehen."}
          </p>
          <Button
            className="rounded-full mt-5 h-11 px-5"
            data-testid="strategies-empty-create"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Neue Strategie
          </Button>
        </div>
      ) : (
        <section
          className="grid grid-cols-1 lg:grid-cols-2 gap-5"
          data-testid="strategies-grid"
        >
          {list.map((s) => (
            <article
              key={s.id}
              className="card-surface p-6 group cursor-pointer hover:-translate-y-[1px] transition-transform duration-200"
              data-testid={`strategy-${s.id}`}
              onClick={() => navigate(`/app/strategien/${s.id}`)}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.trade_types || []).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary-foreground"
                      >
                        {TRADE_LABEL[t] || t}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-heading text-xl mt-2">{s.name}</h3>
                  {s.status === "entwurf" && (
                    <div className="text-[10px] uppercase tracking-wider text-primary-foreground/80 mt-1">Klicken zum Weiterbearbeiten</div>
                  )}
                </div>
                <Badge
                  variant="secondary"
                  className={`rounded-full border ${
                    s.status === "aktiv"
                      ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border-[hsl(122_39%_49%/0.35)]"
                      : s.status === "archiviert"
                      ? "bg-secondary text-muted-foreground border-border"
                      : "bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border-[hsl(32_60%_55%/0.4)]"
                  }`}
                >
                  {s.status}
                </Badge>
              </div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {s.rough_strategy || "Keine Beschreibung hinterlegt."}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-5 text-xs">
                <MiniField label="SL-Idee" value={s.sl_description} />
                <MiniField label="TP-Idee" value={s.tp_description} />
              </div>
              <div className="flex items-center justify-between mt-5 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {(s.coach_messages || []).filter((m) => m.role !== "system_context").length} Coach-Nachrichten
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setResumeStrategy(s); setResumeMode("form"); setDialogOpen(true); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary-foreground inline-flex items-center gap-1"
                    data-testid={`strategy-edit-${s.id}`}
                  >
                    <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} /> bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(s.id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-[hsl(0_66%_72%)] inline-flex items-center gap-1"
                    data-testid={`strategy-delete-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /> löschen
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      )}

      <StrategyDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) { setResumeStrategy(null); setResumeMode("form"); } }}
        onCreated={handleCreated}
        resumeStrategy={resumeStrategy}
        resumeMode={resumeMode}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="strategy-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Strategie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Strategie und der komplette Coach-Verlauf werden dauerhaft entfernt. Das lässt sich nicht rückgängig machen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove(confirmDelete)}
              data-testid="strategy-delete-confirm-btn"
              className="bg-[hsl(0_66%_50%)] hover:bg-[hsl(0_66%_45%)]"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MiniField({ label, value }) {
  return (
    <div className="rounded-lg bg-secondary/60 border border-border p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-xs mt-1 line-clamp-2 whitespace-pre-wrap">
        {value || <span className="italic text-muted-foreground">nicht angegeben</span>}
      </div>
    </div>
  );
}
